// src/components/SignUp.tsx
import React, { useState } from 'react';
import {
    TextField,
    Button,
    Container,
    Typography,
    Box,
    Paper,
    CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';

const SignUp: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false); // New loading state

    const handleSignUp = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage('');
        setLoading(true); // Start loading
        try {
            await api.post('/user/', {
                name,
                email,
                password,
            });
            setSuccessMessage(
                'Please check your email to confirm your account.'
            );
        } catch (error) {
            setErrorMessage('Sign-up failed. Please try again.');
        } finally {
            setLoading(false); // Stop loading
        }
    };

    return (
        <Container maxWidth="sm">
            <Box mt={8} component={Paper} elevation={3} p={4} borderRadius="12px">
                {successMessage ? (
                    <Box textAlign="center" py={4}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50' }} />
                        <Typography
                            variant="h4"
                            gutterBottom
                            sx={{ mt: 2, fontWeight: 'bold', color: '#0D47A1' }}
                        >
                            Account Created!
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.secondary',
                                maxWidth: 400,
                                mx: 'auto',
                                fontSize: '1.2rem', // Increased font size
                            }}
                        >
                            {successMessage}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Typography
                            variant="h4"
                            gutterBottom
                            align="center"
                            sx={{
                                fontWeight: 600,
                                color: '#0D47A1',
                            }}
                        >
                            Create Your Account
                        </Typography>
                        {errorMessage && (
                            <Typography color="error" variant="body2" align="center" gutterBottom>
                                {errorMessage}
                            </Typography>
                        )}
                        <form onSubmit={handleSignUp}>
                            <TextField
                                label="Name"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                InputProps={{
                                    style: {
                                        borderRadius: '8px',
                                    },
                                }}
                                disabled={loading} // Disable while loading
                            />
                            <TextField
                                label="Email"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                InputProps={{
                                    style: {
                                        borderRadius: '8px',
                                    },
                                }}
                                disabled={loading} // Disable while loading
                            />
                            <TextField
                                label="Password"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                InputProps={{
                                    style: {
                                        borderRadius: '8px',
                                    },
                                }}
                                disabled={loading} // Disable while loading
                            />
                            <Box mt={3}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={loading} // Disable button while loading
                                    sx={{
                                        fontWeight: 600,
                                        padding: '0.75rem',
                                        fontSize: '1rem',
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
                                </Button>
                            </Box>
                        </form>
                    </>
                )}
            </Box>
        </Container>
    );
};

export default SignUp;
